
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapState } from 'vuex';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import User, { UserRole, UserRoleType } from '@/types/User';
    import { confirmSwal } from '@/helper/swals';
    import { SweetAlertResult } from 'sweetalert2';
    import { UserValidations, ValidationRules } from '@/store/types';
    import { ListItems, VForm } from '@/types/Components';
    import { UsersFilter } from '@/store/users';
    import { LoadPersonenPayload } from '@/store/personen';

    @Component({
        components: {
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules']),
            ...mapState('users', ['loaders', 'roles', 'validations']),
            ...mapState('authentication', { authUser: 'user' }),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('personen', { personenLoaders: 'loaders' }),
            ...mapGetters('authentication', ['isAllowed']),
        },
        methods: {
            ...mapActions('users', ['updateUser', 'deleteUser']),
            ...mapActions('personen', ['loadPersonen']),
        },
    })
    export default class UsersDetail extends Vue {
        @Prop() visible!: boolean;
        @Prop() value!: User | null;
        @Prop() filter!: UsersFilter;
        @Prop({ default: () => false }) selfEdit!: boolean;

        public $refs!: {
            form: VForm;
        };

        public authUser!: User | null;
        public roles!: UserRoleType[];
        public rules!: ValidationRules;
        public validations!: UserValidations;
        public updateUser!: (payload: { user: User; filter: { deaktiviert: string } }) => void;
        public deleteUser!: (payload: { user: User; filter: { deaktiviert: string } }) => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;

        public user = new User();
        public role = '';
        public prev_username: string | null = '';
        public usernameErrorMessages: string[] = [];
        public passwordErrorMessages: string[] = [];
        public passwordRepeatedErrorMessages: string[] = [];

        public get isEdit(): boolean {
            return this.user.user_id !== null;
        }

        public get isAdmin(): boolean {
            return this.authUser?.isAdmin ?? false;
        }

        public get isAllowedRoleEdit(): boolean {
            return !this.user.hasRoles(['system']) || (this.authUser?.hasRoles(['system']) ?? false);
        }

        public get rollen(): ListItems<UserRoleType> {
            const isSystem = this.authUser?.hasRoles(['system']);

            return this.roles
                .filter((role) => isSystem || (role !== 'system' || !this.isAllowedRoleEdit))
                .map((role) => {
                    return {
                        text: role.slice(0, 1).toUpperCase() + role.slice(1),
                        value: role,
                    };
                });
        }

        public get canClearRoles(): boolean {
            return !this.rollen.some(({ disabled }) => disabled);
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }

                if (this.value) {
                    this.user = this.value.clone();
                }

                this.loadPersonen();

                this.prev_username = this.user.username as string;
                this.usernameErrorMessages = [];
                this.passwordErrorMessages = [];
                this.passwordRepeatedErrorMessages = [];

                const [role] = this.user.roles ?? [];
                this.role = role ?? '';
            }
        }

        public validateUsername(): void {
            this.usernameErrorMessages = [];

            const { username: { min, max } } = this.validations;

            for (const rule of [this.rules.minLength(min), this.rules.maxLength(max)]) {
                const result = rule(this.user.username);
                if (result !== true) {
                    this.usernameErrorMessages.push(result as string);
                    break;
                }
            }
        }

        public validatePassword(): void {
            this.passwordErrorMessages = [];

            if (this.isEdit && this.user.password_new.length === 0) {
                return;
            }

            const { password: { min, max } } = this.validations;

            for (const rule of [this.rules.minLength(min), this.rules.maxLength(max)]) {
                const result = rule(this.user.password_new);
                if (result !== true) {
                    this.passwordErrorMessages.push(result as string);
                    break;
                }
            }
        }

        public validateRepeatedPassword(): void {
            this.passwordRepeatedErrorMessages = [];

            if (this.user.password_new.length === 0 || this.passwordErrorMessages.length > 0) {
                return;
            }

            const repeatedPasswordRule = (value: string | null): boolean | string => {
                return (value === this.user.password_new) || 'Wiederholtes Passwort stimmt nicht exakt überein.';
            };

            for (const rule of [repeatedPasswordRule]) {
                const result = rule(this.user.password_new_repeated);
                if (result !== true) {
                    this.passwordRepeatedErrorMessages.push(result as string);
                    break;
                }
            }
        }

        public onClickSave(): void {
            this.validateUsername();
            this.validatePassword();
            this.validateRepeatedPassword();

            if (!this.$refs.form.validate() ||
                this.usernameErrorMessages.length > 0 ||
                this.passwordErrorMessages.length > 0 ||
                this.passwordRepeatedErrorMessages.length > 0) {
                return;
            }

            this.user.roles = this.role ? [this.role as string & UserRole] : [];

            this.updateUser({ user: this.user, filter: this.filter });
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Benutzer endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteUser({ user: this.user, filter: this.filter });
                    }
                });
        }
    }
